<template>
  <d-row>
    <d-col lg="4">
      <d-card class="card-small mb-4 pt-3">
        <!-- Card Header -->
        <d-card-header class="border-bottom text-center">
          <!-- User Avatar -->
          <div class="mb-3 mx-auto overflow-hidden">
            <upload-image :model.sync="model.settings"></upload-image>
          </div>
          <!-- User Name -->
          <h4 class="mb-0">{{ roleName }}</h4>

          <!-- User Job Title -->
          <span class="text-muted d-block mb-2">
            {{ model.settings.type.name }}
          </span>
        </d-card-header>
      </d-card>
    </d-col>
    <d-col lg="8">
      <d-card class="card-small mb-4">
        <!-- Card Header -->
        <d-card-header class="border-bottom">
          <h6 class="m-0">{{ pageTitle }}</h6>
        </d-card-header>

        <d-list-group flush>
          <d-list-group-item class="p-3">
            <d-row>
              <d-col>
                <d-form @submit.prevent="save" :validated="hasErrors">
                  <d-form-row>
                    <!-- Nome -->
                    <d-col md="6" class="form-group">
                      <label for="feFirstName">
                        {{ $t('user.account.form.name.label') }}
                      </label>
                      <d-form-input
                        type="text"
                        id="feFirstName"
                        :placeholder="$t('user.account.form.name.placeholder')"
                        v-model="model.name"
                      />
                    </d-col>

                    <!-- E-mail -->
                    <d-col md="6" class="form-group">
                      <label for="feEmail">
                        {{ $t('user.account.form.email.label') }}
                      </label>
                      <d-form-input
                        :state="hasErrorFiled('email')"
                        type="email"
                        id="feEmail"
                        :placeholder="$t('user.account.form.email.placeholder')"
                        v-model="model.email"
                      />
                      <d-form-invalid-feedback>
                        {{ $t('user.account.form.email.invalid') }}
                      </d-form-invalid-feedback>
                    </d-col>
                  </d-form-row>

                  <d-form-row v-if="!isCurrentUser">
                    <!-- User type -->
                    <d-col md="6" class="form-group">
                      <label for="feEmail">
                        {{ $t('user.account.form.userType.label') }}
                      </label>
                      <d-select
                        v-model="model.settings.type"
                        @change="setSettings($event, 'user_type_id')"
                      >
                        <option
                          :value="option"
                          v-for="(option, index) in typesList"
                          :key="index"
                        >
                          {{ option.name }}
                        </option>
                      </d-select>
                    </d-col>

                    <!-- Função -->
                    <d-col
                      md="6"
                      class="form-group"
                      v-if="model.settings.user_type_id !== 1"
                    >
                      <label for="feRole">
                        {{ $t('user.account.form.role.label') }}
                      </label>
                      <d-select
                        v-model="model.settings.role"
                        @change="setSettings($event, 'role_id')"
                      >
                        <option
                          :value="option"
                          v-for="(option, index) in rolesList"
                          :key="index"
                        >
                          {{ option.name }}
                        </option>
                      </d-select>
                    </d-col>
                  </d-form-row>
                  <div class="list-group-item px-0 border-bottom-0">
                    <d-form-row>
                      <!-- Old Password -->
                      <d-col md="6" class="form-group" v-if="isCurrentUser">
                        <label for="feOldPassword">
                          {{ $t('user.account.form.oldPassword.label') }}
                        </label>
                        <d-form-input
                          type="password"
                          id="feOldPassword"
                          :placeholder="$t('form.password.placeholder')"
                          v-model="model.old_password"
                        />
                      </d-col>
                      <!-- Old Password -->
                      <d-col
                        md="6"
                        class="form-group"
                        v-if="!isCurrentUser && model.id == null"
                      >
                        <!-- Generate Password -->
                        <label for="fePassword">
                          {{ $t('user.account.form.sendPassword.label') }}
                        </label>
                        <d-checkbox
                          toggle
                          :checked="sendPassword"
                          @change="sendPassword = !sendPassword"
                          class="custom-toggle-sm"
                        >
                          {{ $t('user.account.form.sendPassword.options.yes') }}
                        </d-checkbox>
                      </d-col>
                    </d-form-row>

                    <d-form-row>
                      <template
                        v-if="!sendPassword || model.id || isCurrentUser"
                      >
                        <!-- Password -->
                        <d-col md="6" class="form-group">
                          <label for="fePassword">
                            {{ $t('user.account.form.password.label') }}
                          </label>
                          <d-form-input
                            :state="hasErrorFiled('password')"
                            type="password"
                            id="fePassword"
                            :placeholder="$t('form.password.placeholder')"
                            v-model="model.password"
                          />
                          <d-form-invalid-feedback>
                            {{ $t('user.account.form.password.invalid') }}
                          </d-form-invalid-feedback>
                        </d-col>

                        <!-- Password -->
                        <d-col md="6" class="form-group">
                          <label for="fePasswordConfirmation">
                            {{
                              $t('user.account.form.passwordConfirmation.label')
                            }}
                          </label>
                          <d-form-input
                            :state="hasErrorFiled('password_confirmation')"
                            type="password"
                            id="fePasswordConfirmation"
                            :placeholder="$t('form.password.placeholder')"
                            v-model="model.password_confirmation"
                          />
                        </d-col>
                      </template>
                    </d-form-row>
                  </div>

                  <d-button type="submit" class="btn-accent">
                    {{ $t('user.account.form.submit') }}
                  </d-button>
                </d-form>
              </d-col>
            </d-row>
          </d-list-group-item>
        </d-list-group>
      </d-card>
    </d-col>
  </d-row>
</template>

<script>
import { mapActions } from 'vuex';
import { cloneDeep } from '_utils';
import Events from '_utils/events';
import mixin from '_common/mixins/ValidateForm';
import formatErrors from '_common/mixins/FormatErrors';
import UploadImage from '_shared/UploadImage.vue';
import UserService from '_services/rest/modules/user';
import { MODEL, _LOAD_MODEL_ } from './UserModel';
import { OPTION } from '../../settings/components/SettingsModel';

export default {
  name: 'user-account-details',
  mixins: [mixin, formatErrors],
  components: {
    UploadImage,
  },
  props: {
    title: {
      type: String,
      default: null,
    },
    isCurrentUser: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    pageTitle() {
      return this.title || this.$t('user.account.title');
    },
    rolesList() {
      const { roles } = this.$store.state.settings;
      if (roles && roles.length > 0) {
        return roles;
      }
      return [{ ...OPTION }];
    },
    typesList() {
      const { userTypes } = this.$store.state.settings;
      if (userTypes && userTypes.length > 0) {
        return userTypes;
      }
      return [{ ...OPTION }];
    },
    roleName() {
      if (this.model.settings.user_type_id === 1) {
        return this.$t('user.account.roles.administrator');
      }
      if (!this.model.settings.role.name) {
        return this.$t('user.account.roles.none');
      }
      return this.model.settings.role.name;
    },
  },
  data() {
    return {
      model: cloneDeep(MODEL),
      loading: false,
      sendPassword: true,
      errors: {
        email: { required: false },
        password: { equals: false, field: 'password_confirmation' },
        password_confirmation: { equals: false, field: 'password' },
      },
    };
  },
  methods: {
    ...mapActions([
      'getRoles',
      'activeAlert',
      'setUserEdit',
      'getUserTypes',
      'saveUser',
    ]),
    getRoleId() {
      if (this.model.settings.role.id) {
        [this.model.settings.role] = this.rolesList;
      }

      return this.model.settings.role.id;
    },
    setSettings(event, field = 'role_id') {
      setTimeout(() => {
        this.model.settings.role_id = null;
        if (field === 'user_type_id' && event.id !== 1) {
          this.model.settings.role_id = this.getRoleId();
        }
        this.model.settings[field] = event.id;
        this.setUserEdit(this.model);
      }, 20);
    },
    getOptions() {
      if (!this.$store.state.settings.roles) {
        this.getRoles();
      }
      if (!this.$store.state.settings.userTypes) {
        this.getUserTypes();
      }
    },
    save() {
      if (this.checkForm()) {
        if (this.isCurrentUser) {
          return this.saveUser(this.model)
            .then(response => {
              this.activeAlert({ message: response.message, theme: 'success' });
            })
            .catch(response =>
              this.activeAlert({ message: response.message, theme: 'danger' }),
            );
        }
        return UserService.save(this.model)
          .then(response => {
            this.activeAlert({ message: response.message, theme: 'success' });
            this.$router.push(`/admin/usuarios/${response.data.id}`);
          })
          .catch(error => {
            const errors = this.formatErrors(error.data);
            this.activeAlert({ message: errors, theme: 'danger' });
          });
      }
      return false;
    },
  },
  mounted() {
    if (!this.isCurrentUser) {
      this.getOptions();
    }
    Events.$on(_LOAD_MODEL_, user => {
      this.model = cloneDeep(user);
    });
  },
};
</script>
